import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { I18nProvider } from '@/hooks/useI18n';
import { usePageTracking } from '@/hooks/usePageTracking';
// Home + pilares carregadas de forma EAGER (não-lazy): no primeiro render sob createRoot
// elas não suspendem, então o React comita a página inteira de uma vez sobre o HTML
// pré-renderizado — sem flash do spinner e sem colapso/re-expansão (CLS ~0).
import { HomePage } from '@/pages/HomePage';
import { PraiasPage } from '@/pages/PraiasPage';
import { PousadasPage } from '@/pages/PousadasPage';
import { RestaurantesPage } from '@/pages/RestaurantesPage';
import { PontosTuristicosPage } from '@/pages/PontosTuristicosPage';
import { OQueFazerPage } from '@/pages/OQueFazerPage';
import { BaresPage } from '@/pages/BaresPage';
import { TrilhasPage } from '@/pages/TrilhasPage';
import { PasseiosBarcoPage } from '@/pages/PasseiosBarcoPage';
import './App.css';

/* ═══════════════════════════════════════════
   Code-splitting por rota — reduz chunk inicial
   Cada página é carregada sob demanda
   ═══════════════════════════════════════════ */

const GuiaCompletoPage = lazy(() =>
  import('@/pages/GuiaCompletoPage').then(m => ({ default: m.GuiaCompletoPage }))
);
const ExperienciasPage = lazy(() =>
  import('@/pages/ExperienciasPage').then(m => ({ default: m.ExperienciasPage }))
);
// OQueFazerPage: import EAGER (pilar — CWV/CLS 0)
const CategoryListPage = lazy(() =>
  import('@/pages/CategoryListPage').then(m => ({ default: m.CategoryListPage }))
);
const EntityDetailPage = lazy(() =>
  import('@/pages/EntityDetailPage').then(m => ({ default: m.EntityDetailPage }))
);
const ComoChegar = lazy(() => import('@/pages/ComoChegar').then(m => ({ default: m.ComoChegar })));
const NovaBrasiliaVsEncantadasPage = lazy(() =>
  import('@/pages/NovaBrasiliaVsEncantadasPage').then(m => ({
    default: m.NovaBrasiliaVsEncantadasPage,
  }))
);
const BlogListPage = lazy(() =>
  import('@/pages/BlogListPage').then(m => ({ default: m.BlogListPage }))
);
const BlogPostPage = lazy(() =>
  import('@/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage }))
);
const TabelaMarePage = lazy(() =>
  import('@/pages/TabelaMarePage').then(m => ({ default: m.TabelaMarePage }))
);
const PrevisaoDoTempoPage = lazy(() =>
  import('@/pages/PrevisaoDoTempoPage').then(m => ({ default: m.PrevisaoDoTempoPage }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage }))
);

function PageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-sand-200 border-t-teal-deep rounded-full animate-spin" />
    </div>
  );
}

function LocalizedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      {/* /guia-completo consolidado na home via 301 (ver public/_redirects) */}
      <Route path="/experiencias" element={<ExperienciasPage />} />
      <Route path="/o-que-fazer" element={<OQueFazerPage />} />
      <Route path="/como-chegar" element={<ComoChegar />} />
      <Route path="/nova-brasilia-vs-encantadas" element={<NovaBrasiliaVsEncantadasPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/tabela-de-mare-2026" element={<TabelaMarePage />} />
      <Route path="/previsao-do-tempo" element={<PrevisaoDoTempoPage />} />

      {/* URLs pilares amigáveis — renderizam as mesmas páginas sem redirect 301 */}
      <Route path="/como-chegar-na-ilha-do-mel" element={<ComoChegar />} />
      <Route path="/travessia-para-a-ilha-do-mel" element={<ComoChegar />} />
      <Route path="/pousadas-ilha-do-mel" element={<PousadasPage />} />
      <Route path="/restaurantes-ilha-do-mel" element={<RestaurantesPage />} />
      <Route path="/o-que-fazer-na-ilha-do-mel" element={<OQueFazerPage />} />
      <Route path="/praias-ilha-do-mel" element={<PraiasPage />} />
      <Route path="/pontos-turisticos-ilha-do-mel" element={<PontosTuristicosPage />} />
      <Route path="/o-que-fazer-ilha-do-mel" element={<OQueFazerPage />} />
      <Route path="/bares-ilha-do-mel" element={<BaresPage />} />
      <Route path="/trilhas-ilha-do-mel" element={<TrilhasPage />} />
      <Route path="/passeios-de-barco-ilha-do-mel" element={<PasseiosBarcoPage />} />
      <Route path="/melhores-restaurantes-na-ilha-do-mel" element={<RestaurantesPage />} />
      <Route path="/melhores-bares-na-ilha-do-mel" element={<CategoryListPage category="restaurantes" canonicalPath="/melhores-bares-na-ilha-do-mel" />} />
      <Route path="/previsao-do-tempo-na-ilha-do-mel" element={<PrevisaoDoTempoPage />} />
      <Route path="/roteiro/1-dia-ilha-do-mel" element={<GuiaCompletoPage />} />
      <Route path="/roteiro/2-dias-ilha-do-mel" element={<GuiaCompletoPage />} />
      <Route path="/roteiro/3-dias-ilha-do-mel" element={<GuiaCompletoPage />} />

      <Route path="/:category" element={<CategoryListPage />} />
      <Route path="/:category/:handle" element={<EntityDetailPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function AppRoutes() {
  usePageTracking();
  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        {/* Idiomas com prefixo DEVEM vir antes da rota genérica */}
        <Route path="/es/*" element={<LocalizedRoutes />} />
        <Route path="/en/*" element={<LocalizedRoutes />} />
        <Route path="/de/*" element={<LocalizedRoutes />} />
        <Route path="/fr/*" element={<LocalizedRoutes />} />
        {/* pt-BR (default) */}
        <Route path="/*" element={<LocalizedRoutes />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <I18nProvider>
          <Layout>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </Layout>
        </I18nProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
